import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IconModule } from '@shared/icon/icon.module';
import {
  AppNotification,
  NotificationBellTab,
  NotificationService,
  bellTabToStatus,
} from '../../../core/services/notification/notification.service';
import {
  messagePreview as formatMessagePreview,
  notificationIconKind as resolveNotificationIconKind,
  type NotificationVisualKind,
} from '../../notification/notification-ui.utils';

@Component({
  selector: 'app-notification-dropdown-panel',
  standalone: true,
  imports: [CommonModule, IconModule],
  templateUrl: './notification-dropdown-panel.component.html',
  styleUrl: './notification-dropdown-panel.component.scss',
})
export class NotificationDropdownPanelComponent implements OnInit {
  @Output() readonly closed = new EventEmitter<void>();

  readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeTab = signal<NotificationBellTab>('all');
  readonly listLoading = signal(false);
  readonly listItems = signal<AppNotification[]>([]);

  readonly tabs: { id: NotificationBellTab; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread' },
    { id: 'trash', label: 'Trash' },
  ];

  ngOnInit(): void {
    void this.loadList();
    this.notificationService.listRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.loadList());
  }

  setTab(tab: NotificationBellTab): void {
    this.activeTab.set(tab);
    void this.loadList();
  }

  trackById(_index: number, n: AppNotification): string {
    return n.id;
  }

  async onMarkRead(id: string, event: Event): Promise<void> {
    event.stopPropagation();
    await this.notificationService.markAsRead(id);
    this.listItems.update((list) =>
      list.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
    );
  }

  onMoveToTrash(id: string, event: Event): void {
    event.stopPropagation();
    const tab = this.activeTab();
    if (tab === 'trash') {
      this.notificationService.dismissFromTrash(id);
    } else {
      this.notificationService.moveToTrash(id);
    }
  }

  async onMarkAllRead(event: Event): Promise<void> {
    event.stopPropagation();
    await this.notificationService.markAllAsRead();
    this.listItems.update((list) => list.map((n) => ({ ...n, isRead: true })));
  }

  openNotificationCenter(event: Event): void {
    event.stopPropagation();
    this.closed.emit();
    void this.router.navigate(['/notification-center'], {
      queryParams: {
        status: bellTabToStatus(this.activeTab()),
      },
    });
  }

  closePanel(event: Event): void {
    event.stopPropagation();
    this.closed.emit();
  }

  showMarkReadAction(notification: AppNotification): boolean {
    return this.activeTab() !== 'trash' && !notification.isRead;
  }

  notificationIconKind(n: AppNotification): NotificationVisualKind {
    return resolveNotificationIconKind(n);
  }

  messagePreview(n: AppNotification): string {
    return formatMessagePreview(n);
  }

  private async loadList(): Promise<void> {
    this.listLoading.set(true);
    try {
      const res = await this.notificationService.fetchNotifications({
        status: bellTabToStatus(this.activeTab()),
        page: 1,
        pageSize: 50,
      });
      this.listItems.set(res.docs);
    } catch {
      this.listItems.set([]);
    } finally {
      this.listLoading.set(false);
    }
  }
}
